// Public /pricing page (pt /pricing, en /en/pricing, es /es/pricing).
// Reuses the live plans (PlanCards) and builds the comparison table from
// the same data — limits + the gated feature flags — so it can't drift
// from what the API actually offers. No monthly/annual toggle yet: the
// backend has no annual pricing, and inventing values would mislead.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Head } from "vite-react-ssg";

import { Button } from "@/components/ui/Button";
import { PlanCards } from "@/components/marketing/PlanCards";
import { PricingToggle } from "@/components/marketing/PricingToggle";
import { Eyebrow, Section, SectionHeading } from "@/components/marketing/Section";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { SiteNav } from "@/components/marketing/SiteNav";
import { usePlans, type BillingPeriod } from "@/hooks/usePlans";
import { LOCALES, LOCALE_HREFLANG, localeUrl } from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";
import { useAuthStore } from "@/stores/authStore";

const PAGE = "pricing";

export function PricingPage() {
  const navigate = useNavigate();
  const locale = useLocale();
  const d = useDict();
  const t = d.pricing;
  const plans = usePlans();
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));

  const limit = (n: number) =>
    n === 0 ? d.landing.pricing.unlimited : n.toString();
  // Feature rows = the gated flags, in dictionary order, that any plan exposes.
  const featureKeys = (
    Object.keys(d.landing.featureLabels) as (keyof typeof d.landing.featureLabels)[]
  ).filter((k) => plans.some((p) => k in p.features));

  const th = "p-3 text-left font-mono text-[11px] uppercase tracking-[0.08em]";
  const td = "p-3 border-t border-stroke text-center";

  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <html lang={LOCALE_HREFLANG[locale]} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={localeUrl(locale, PAGE)} />
        <meta property="og:url" content={localeUrl(locale, PAGE)} />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        {LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={LOCALE_HREFLANG[loc]}
            href={localeUrl(loc, PAGE)}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={localeUrl("pt", PAGE)} />
      </Head>

      <SiteNav page={PAGE} />

      {/* ── Hero + plan cards (one section, no double gap) ──── */}
      <Section alt width="1200px">
        <div className="text-center max-w-[820px] mx-auto mb-8 sm:mb-10">
          <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          <h1
            className="font-display font-bold text-content mb-6 leading-[1.1]"
            style={{ fontSize: "clamp(30px, 5vw, 52px)" }}
          >
            {t.hero.title}
          </h1>
          <p className="font-sans text-[16px] sm:text-[18px] text-muted max-w-[640px] mx-auto leading-[1.6]">
            {t.hero.lead}
          </p>
        </div>
        <PricingToggle value={period} onChange={setPeriod} />
        <PlanCards plans={plans} period={period} />
      </Section>

      {/* ── Comparison table (from live data) ───────────────── */}
      {plans.length > 0 && (
        <Section width="1000px">
          <SectionHeading
            eyebrow={t.comparison.eyebrow}
            title={t.comparison.title}
          />
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={`${th} text-muted`} />
                  {plans.map((p) => (
                    <th
                      key={p.slug}
                      scope="col"
                      className={`${th} text-center ${p.slug === "pro" ? "text-accent" : "text-content"}`}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <GroupRow label={t.comparison.groupLimits} span={plans.length + 1} />
                <LimitRow label={t.comparison.engagements} td={td} plans={plans} get={(p) => limit(p.max_engagements)} />
                <LimitRow label={t.comparison.scans} td={td} plans={plans} get={(p) => limit(p.max_scans_per_month)} />
                <LimitRow label={t.comparison.users} td={td} plans={plans} get={(p) => limit(p.max_users)} />

                <GroupRow label={t.comparison.groupFeatures} span={plans.length + 1} />
                {featureKeys.map((k) => (
                  <tr key={k}>
                    <th scope="row" className="p-3 border-t border-stroke text-left font-sans text-[13px] text-muted font-normal">
                      {d.landing.featureLabels[k]}
                    </th>
                    {plans.map((p) => (
                      <td key={p.slug} className={td}>
                        {p.features[k] ? (
                          <span className="text-success">✓</span>
                        ) : (
                          <span className="text-dim">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ── Final CTA ───────────────────────────────────────── */}
      <Section alt width="800px">
        <div className="text-center">
          <Eyebrow>{t.cta.eyebrow}</Eyebrow>
          <h2 className="font-display text-[28px] sm:text-[38px] font-bold text-content mb-4 leading-[1.2]">
            {t.cta.title}
          </h2>
          <p className="font-sans text-[15px] sm:text-[16px] text-muted mb-8 leading-[1.6]">
            {t.cta.body}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              variant="primary"
              onClick={() => navigate(isAuthed ? "/app" : "/login")}
            >
              {t.cta.buttonFree}
            </Button>
            <Button
              variant="default"
              onClick={() =>
                window.open(
                  "mailto:sales@truehacking.ai?subject=" +
                    encodeURIComponent("Enterprise plan"),
                  "_blank",
                )
              }
            >
              {t.cta.buttonSales}
            </Button>
          </div>
        </div>
      </Section>

      <SiteFooter />
    </div>
  );
}

function GroupRow({ label, span }: { label: string; span: number }) {
  return (
    <tr>
      <th
        scope="colgroup"
        colSpan={span}
        className="p-3 pt-6 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-accent"
      >
        {label}
      </th>
    </tr>
  );
}

function LimitRow<P>({
  label,
  td,
  plans,
  get,
}: {
  label: string;
  td: string;
  plans: P[];
  get: (p: P) => string;
}) {
  return (
    <tr>
      <th
        scope="row"
        className="p-3 border-t border-stroke text-left font-sans text-[13px] text-muted font-normal"
      >
        {label}
      </th>
      {plans.map((p, i) => (
        <td key={i} className={`${td} font-mono text-[12px] text-content`}>
          {get(p)}
        </td>
      ))}
    </tr>
  );
}
